import { toast } from "sonner";
import { ExternalLink } from "lucide-react";
import { api, errMsg } from "@/lib/api";
import { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";

const STYLES = {
  known_good: "bg-emerald-50 text-emerald-700 border-emerald-200", deployed: "bg-emerald-50 text-emerald-700 border-emerald-200", ready: "bg-emerald-50 text-emerald-700 border-emerald-200", ok: "bg-emerald-50 text-emerald-700 border-emerald-200",
  running: "bg-blue-50 text-blue-700 border-blue-200", claimed: "bg-blue-50 text-blue-700 border-blue-200", queued: "bg-blue-50 text-blue-700 border-blue-200", deploying: "bg-blue-50 text-blue-700 border-blue-200", importing: "bg-blue-50 text-blue-700 border-blue-200",
  awaiting_approval: "bg-amber-50 text-amber-700 border-amber-200", needs_clarification: "bg-amber-50 text-amber-700 border-amber-200", planned: "bg-amber-50 text-amber-700 border-amber-200", paused_budget: "bg-amber-50 text-amber-700 border-amber-200", paused_external_change: "bg-amber-50 text-amber-700 border-amber-200", warn: "bg-amber-50 text-amber-700 border-amber-200", created: "bg-amber-50 text-amber-700 border-amber-200",
  failed: "bg-rose-50 text-rose-700 border-rose-200", deploy_failed: "bg-rose-50 text-rose-700 border-rose-200", unhealthy: "bg-rose-50 text-rose-700 border-rose-200", error: "bg-rose-50 text-rose-700 border-rose-200", blocked: "bg-rose-50 text-rose-700 border-rose-200", release_blocked: "bg-rose-50 text-rose-700 border-rose-200", paused_smoke_failed: "bg-rose-50 text-rose-700 border-rose-200", paused_ui_failed: "bg-rose-50 text-rose-700 border-rose-200", passed: "bg-emerald-50 text-emerald-700 border-emerald-200", sent: "bg-emerald-50 text-emerald-700 border-emerald-200", skipped: "bg-slate-100 text-slate-600 border-slate-200",
  staged: "bg-violet-50 text-violet-700 border-violet-200", paused_repair: "bg-amber-50 text-amber-700 border-amber-200", paused_visual: "bg-amber-50 text-amber-700 border-amber-200", changed: "bg-amber-50 text-amber-700 border-amber-200", accepted: "bg-emerald-50 text-emerald-700 border-emerald-200", no_baseline: "bg-slate-100 text-slate-600 border-slate-200", enabled: "bg-emerald-50 text-emerald-700 border-emerald-200", disabled: "bg-slate-100 text-slate-500 border-slate-200", approved: "bg-emerald-50 text-emerald-700 border-emerald-200", degraded: "bg-amber-50 text-amber-700 border-amber-200", billing: "bg-orange-50 text-orange-700 border-orange-200", down: "bg-rose-50 text-rose-700 border-rose-200", missing: "bg-rose-50 text-rose-700 border-rose-200", unknown: "bg-slate-100 text-slate-500 border-slate-200", applying: "bg-blue-50 text-blue-700 border-blue-200", rolled_back: "bg-rose-50 text-rose-700 border-rose-200", healthy: "bg-emerald-50 text-emerald-700 border-emerald-200", archived: "bg-slate-100 text-slate-600 border-slate-200", superseded: "bg-slate-100 text-slate-500 border-slate-200",
};
const LIVE = new Set(["running", "claimed", "queued", "deploying", "importing", "applying"]);

export function StatusBadge({ status, testId }) {
  const cls = STYLES[status] || "bg-slate-100 text-slate-600 border-slate-200";
  return (
    <span data-testid={testId || `status-badge-${status}`} className={`pill ${cls}`}>
      <span className={`h-1.5 w-1.5 rounded-full bg-current ${LIVE.has(status) ? "pulse-dot" : ""}`} />
      {String(status || "unknown").replaceAll("_", " ")}
    </span>
  );
}

export function TypedConfirm({ open, onOpenChange, phrase, title, description, onConfirm, danger, testId = "typed-confirm" }) {
  const [val, setVal] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (!open) setVal(""); }, [open, phrase]);
  const changeOpen = next => { if (!busy) onOpenChange(next); };
  const go = async () => { setBusy(true); try { await onConfirm(val); onOpenChange(false); setVal(""); } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); } };
  return (
    <Dialog open={open} onOpenChange={changeOpen}>
      <DialogContent data-testid={`${testId}-dialog`}>
        <DialogHeader><DialogTitle>{title}</DialogTitle><DialogDescription>{description}</DialogDescription></DialogHeader>
        <p className="text-sm text-slate-600">Type <code className="mono bg-slate-100 px-1.5 py-0.5 rounded text-slate-900">{phrase}</code> to continue.</p>
        <input data-testid={`${testId}-input`} aria-label={"Type " + phrase + " to confirm"} className="field mono" value={val} disabled={busy} onChange={(e) => setVal(e.target.value)} placeholder={phrase} autoFocus />
        <DialogFooter>
          <button className="btn-secondary" onClick={() => changeOpen(false)} disabled={busy} data-testid={`${testId}-cancel`}>Cancel</button>
          <button className={danger ? "btn-danger" : "btn-primary"} disabled={val !== phrase || busy} onClick={go} data-testid={`${testId}-submit`}>{busy ? "Working…" : "Confirm"}</button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function Empty({ title, hint, testId }) {
  return <div data-testid={testId} className="doc-card p-10 text-center"><p className="text-sm font-medium text-slate-800">{title}</p>{hint && <p className="text-sm text-slate-500 mt-1">{hint}</p>}</div>;
}

export function PageHeader({ eyebrow, title, children }) {
  return (
    <div className="flex flex-wrap items-end justify-between gap-3 sm:gap-6 mb-6 sm:mb-8 fade-up">
      <div className="min-w-0 max-w-full break-words">{eyebrow && <p className="eyebrow mb-1.5">{eyebrow}</p>}<h1 className="text-3xl sm:text-4xl font-semibold text-slate-900">{title}</h1></div>
      <div className="flex flex-wrap items-center gap-2 max-w-full">{children}</div>
    </div>
  );
}

const isValidPreviewUrl = (url) => {
  if (!url || typeof url !== "string") return false;
  try {
    const parsed = new URL(url);
    return ["http:", "https:"].includes(parsed.protocol);
  } catch {
    return false;
  }
};

export function projectPreviewUrl(project, releases = []) {
  const candidates = [
    project?.preview_url,
    project?.deploy_url,
    project?.deployment_url,
    project?.known_good_release?.deploy?.url,
    ...releases.filter((r) => r?.id === project?.known_good_release_id).map((r) => r?.deploy?.url),
    ...releases.filter((r) => r?.status === "known_good").map((r) => r?.deploy?.url),
  ];
  return candidates.find(isValidPreviewUrl) || "";
}

export function ProjectPreviewButton({ project, releases = [], className = "", testId = "project-preview-button" }) {
  const [busy, setBusy] = useState(false);
  const [reason, setReason] = useState("");
  const open = async () => {
    setBusy(true); setReason("");
    try {
      const { data } = await api.post(`/projects/${project.id}/preview`);
      if (!isValidPreviewUrl(data.url) || data.status === "failed") throw new Error(data.reason || data.note || "Preview could not start.");
      if (data.access?.invite_code) setReason("Preview invitation: " + data.access.invite_code);
      else if (data.access?.email) setReason("Preview sign-in: " + data.access.email + " · password: " + data.access.password);
      // Preview navigation must work inside embedded/in-app browsers and on mobile.
      // Opening a blank tab before an async API call is blocked or discarded by
      // several browser shells, so navigate the current tab after the preview is ready.
      const opened = window.open(data.url, "_self");
      if (!opened) window.location.assign(data.url);
    } catch (e) {
      const message = e.message && !e.response ? e.message : errMsg(e);
      setReason(message); toast.error(message);
    } finally { setBusy(false); }
  };
  return <span className="inline-flex flex-col max-w-full gap-1"><button type="button" onClick={open} disabled={busy} className={`btn-secondary !py-1.5 !px-2 text-xs ${className}`} title={reason || "Start or open this project's preview"} data-testid={testId}><ExternalLink size={13} />{busy ? "Starting preview…" : "Preview"}</button>{reason && <span className="text-xs text-rose-700 break-words" role="status" data-testid={testId + "-reason"}>{reason}</span>}</span>;
}
